import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  Input,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { AuthService, AutoComplete } from 'src/app/services/auth/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { handleServerSideValidation } from '@back/shared/utils/server-side-validation';
import { ToastrService } from 'ngx-toastr';
import { MustMatch } from '@core/utils/must-match.validation';
import { debounceTime, filter, finalize, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { formatDateMdy } from '@back/shared/utils/date-utils';
import { SettingsService } from '@app/services/settings/settings.service';
import { Subject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { WINDOW } from '@ng-web-apis/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AutocompleteLibModule } from 'angular-ng-autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AsyncPipe } from '@angular/common';
import { AuthTabButtonsComponent } from '../components/auth-tab-buttons/auth-tab-buttons.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatNativeDateModule } from '@angular/material/core';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AuthTabButtonsComponent,
    ReactiveFormsModule,
    FormsModule,
    MatDatepickerModule,
    AutocompleteLibModule,
    MatCheckboxModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatMomentDateModule,
    MatNativeDateModule,
  ],
})
export class RegisterComponent {
  settings = inject(SettingsService).settings;

  @Input() darkButtons: boolean = true;

  @ViewChild('autocompleteRef', { static: false }) autocompleteRef: any;

  isSubmitted: boolean = false;
  isLoading = signal<boolean>(false);
  isCompany: boolean = false;
  startDate = new Date(1990, 0, 1);
  access_token: string = '';

  keyword = 'name';
  initialValue: any;
  isAutoCompleteDisabled: boolean = false;
  nations: AutoComplete[] = [];
  provinces: AutoComplete[] = [];

  readonly nationSearchQuery$ = new Subject<string>();
  readonly provinceSearchQuery$ = new Subject<string>();

  registerForm: UntypedFormGroup = this._fb.group(
    {
      first_name: ['', [Validators.required]],
      last_name: ['', [Validators.required]],
      birthdate: ['', [Validators.required]],
      location: [''],

      //Common fields are below

      email: ['', [Validators.required, Validators.email]],
      phone_number: ['', [Validators.required]],

      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/),
        ],
      ],
      password_confirmation: ['', [Validators.required, Validators.minLength(8)]],

      fiscal_code: ['', [Validators.required, Validators.minLength(11), Validators.maxLength(16)]],
      nation: ['', [Validators.required]],
      province: ['', [Validators.required]],
      zip: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
      city: ['', [Validators.required]],
      street: ['', [Validators.required]],

      general_contact: [false, Validators.requiredTrue],
      profile_privacy: [false],
      marketing_privacy: [false],
      thirdparty_privacy: [false],

      session_id: [''],
    },
    MustMatch('password', 'password_confirmation')
  );

  companyForm: UntypedFormGroup = this._fb.group(
    {
      business_name: ['', [Validators.required]],
      vat_number: ['', [Validators.required, Validators.minLength(11), Validators.maxLength(11)]],
      pec_email_address: ['', [Validators.required, Validators.email]],
      sdi_code: ['', [Validators.maxLength(7)]],
      // privacy_consent: [false, [Validators.requiredTrue]],

      // Below fields are common

      email: ['', [Validators.required, Validators.email]],
      phone_number: ['', [Validators.required]],

      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          //Must contain 1 uppercase letter, 1 special character and 1 number
          Validators.pattern(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/),
        ],
      ],
      password_confirmation: ['', [Validators.required, Validators.minLength(8)]],

      fiscal_code: ['', [Validators.required, Validators.minLength(11), Validators.maxLength(16)]],
      nation: ['', [Validators.required]],
      province: ['', [Validators.required]],
      zip: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
      city: ['', [Validators.required]],
      street: ['', [Validators.required]],

      general_contact: [false, Validators.requiredTrue],
      profile_privacy: [false],
      marketing_privacy: [false],
      thirdparty_privacy: [false],

      session_id: [''],
    },
    MustMatch('password', 'password_confirmation')
  );

  constructor(
    private _fb: UntypedFormBuilder,
    private authService: AuthService,
    private toast: ToastrService,
    private router: Router,
    private modalService: NgbModal,
    @Inject(WINDOW) private window: Window,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.fetchProvincesOnDemand();
    this.fetchNationsOnDemand();
  }

  selectEvent(nation: any): void {
    // do something with selected item
    this.registerForm.patchValue({
      nation: nation.code,
    });
  }

  onSubmit() {
    this.isSubmitted = true;

    if (this.registerForm.invalid) {
      this.toast.error('Controlla eventuali errori nel modulo e invia di nuovo!');
      return;
    }

    let payload = this.registerForm.value;
    payload = {
      ...payload,
      fiscal_code: this.getValue(this.registerForm, 'fiscal_code'),
    };
    console.log('Payload', payload);
    if (payload?.birthdate && payload.birthdate !== '') {
      payload = {
        ...payload,

        birthdate: formatDateMdy(payload.birthdate),
      };
    }

    this.isLoading.set(true);
    this.authService
      .registerUser(this.access_token, payload)
      .pipe(
        take(1),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe(
        res => {
          this.router.navigate(['/auth/login']).then(() => {
            this.toast.success('Sei stato registrato con successo.');
          });
        },
        error => {
          if (error instanceof HttpErrorResponse) {
            this._handleHttpError(error, this.registerForm);
          }
          this.cdr.markForCheck();
        }
      );
  }

  registerCompany() {
    this.isSubmitted = true;

    if (this.companyForm.invalid) {
      this.toast.error('Controlla eventuali errori nel modulo e invia di nuovo!');
      return;
    }

    let payload = this.companyForm.value;
    payload = {
      ...payload,
      fiscal_code: this.getValue(this.companyForm, 'fiscal_code'),
    };

    this.isLoading.set(true);
    this.authService
      .registerCompany(this.access_token, payload)
      .pipe(
        take(1),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe(
        res => {
          this.router.navigate(['/auth/login']).then(() => {
            this.toast.success('Sei stato registrato con successo.');
          });
        },
        error => {
          if (error instanceof HttpErrorResponse) {
            this._handleHttpError(error, this.companyForm);
          }
          this.cdr.markForCheck();
        }
      );
  }

  fetchProvincesOnDemand() {
    this.provinceSearchQuery$
      .pipe(
        filter(query => query.length >= 1),
        debounceTime(200),
        // startWith(''),
        switchMap((query: string) => this.authService.getAutocomplete({ query }, 'provinces'))
      )
      .subscribe(response => {
        this.provinces = response;
        this.cdr.markForCheck();
      });
  }

  fetchNationsOnDemand() {
    this.nationSearchQuery$
      .pipe(
        filter(query => query.length >= 1),
        debounceTime(200),
        // startWith(''),
        switchMap((query: string) => this.authService.getAutocomplete({ query }, 'nations'))
      )
      .subscribe(response => {
        this.nations = response;
        this.cdr.markForCheck();
      });
  }

  async openConditionsModal(): Promise<void> {
    const { InfoNoteModalComponent } = await import('@app/shared/info-note-modal/info-note-modal.component');
    this.modalService.open(InfoNoteModalComponent, {
      size: 'xl',
      scrollable: true,
      centered: true,
    });
  }

  openPrivacyPolicyOnNewTab(): void {
    const url: string = this.router.createUrlTree(['/', 'privacy-policy']).toString();
    this.window.open(url, '_blank');
  }

  isInvalid(form: UntypedFormGroup, control: string): boolean | undefined {
    return (this.isSubmitted && form.get(control)?.errors) as boolean;
  }

  getError(form: UntypedFormGroup, control: string): ValidationErrors | null {
    return form.get(control)?.errors as ValidationErrors;
  }

  getValue(form: UntypedFormGroup, control: string): any {
    return form.get(control)?.value;
  }

  setValue(form: UntypedFormGroup, control: string, el: AutoComplete): void {
    form.get(control)?.setValue(el.code);
  }

  setNationsValue(form: UntypedFormGroup, control: string, el: AutoComplete): void {
    let esteroPayload = {
      id: 32,
      name: 'ESTERO',
      code: 'ES',
      region_id: null,
      created_at: null,
      updated_at: null,
    };
    if (el.code === 'ITA') {
      if (this.isAutoCompleteDisabled) {
        this.isAutoCompleteDisabled = false;
        this.initialValue = null;
        this.clearValue(form, 'province');
        this.autocompleteRef.query = '';
        form.get('fiscal_code')?.enable();
      }
      form.get(control)?.setValue(el.code);
    } else {
      form.get(control)?.setValue(el.code);
      form.get('province')?.setValue(esteroPayload.code);
      this.initialValue = esteroPayload;
      this.isAutoCompleteDisabled = true;
      this.clearValue(form, 'fiscal_code');
      form.get('fiscal_code')?.setValue('');
      form.get('fiscal_code')?.disable();
    }
  }

  clearNationsValue(form: UntypedFormGroup, control: string, value: any = ''): void {
    if (this.isAutoCompleteDisabled) {
      this.isAutoCompleteDisabled = false;
      this.initialValue = null;
      this.clearValue(form, 'province');
      this.autocompleteRef.query = '';
      form.get('fiscal_code')?.enable();
    }
    form.get(control)?.setValue(value);
  }

  clearValue(form: UntypedFormGroup, control: string, value: any = ''): void {
    form.get(control)?.setValue(value);
  }

  serverSideError(form: UntypedFormGroup, control: string): string | null {
    return form.get(control)?.getError('serverSideError');
  }

  ngOnDestroy() {
    this.provinceSearchQuery$.unsubscribe();
    this.nationSearchQuery$.unsubscribe();
  }

  private _addAgencyField() {
    //Add agency fields
    this.registerForm.addControl('business_name', new UntypedFormControl('', [Validators.required]));
    this.registerForm.addControl(
      'vat_number',
      new UntypedFormControl('', [Validators.required, Validators.maxLength(12)])
    );
    this.registerForm.addControl(
      'pec_email_address',
      new UntypedFormControl('', [Validators.required, Validators.email])
    );
    this.registerForm.addControl('sdi_code', new UntypedFormControl(''));
    this.registerForm.addControl('privacy_consent', new UntypedFormControl(false, [Validators.requiredTrue]));

    // remove client fields
    this.registerForm.removeControl('first_name');
    this.registerForm.removeControl('last_name');
    this.registerForm.removeControl('birthdate');
    this.registerForm.removeControl('location');
  }

  private _addClientField() {
    // add client fields
    this.registerForm.addControl('first_name', new UntypedFormControl('', [Validators.required]));
    this.registerForm.addControl('last_name', new UntypedFormControl('', [Validators.required]));
    this.registerForm.addControl('birthdate', new UntypedFormControl('', [Validators.required]));
    this.registerForm.addControl('location', new UntypedFormControl('', [Validators.required]));

    // remove agency fields
    this.registerForm.removeControl('business_name');
    this.registerForm.removeControl('vat_number');
    this.registerForm.removeControl('pec_email_address');
    this.registerForm.removeControl('sdi_code');
    this.registerForm.removeControl('privacy_consent');
  }

  private _handleHttpError(error: Error, form: UntypedFormGroup): void {
    if (!(error instanceof HttpErrorResponse)) {
      return;
    }

    if (error.status === 401) {
      this.toast.error(error.error.message);
    }

    const unhandledErrors = handleServerSideValidation(error, form);
    if (unhandledErrors) {
      this.toast.error(error.statusText, unhandledErrors);
    }
  }
}